import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { CandidatFicheActions } from '../components/candidat/CandidatFicheActions';
import { DoublonsCandidats } from '../components/candidat/DoublonsCandidats';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Plus, 
  Search, 
  Users,
  Mail,
  Phone,
  Building,
  Pencil,
  Trash2,
  MapPin,
  Calendar,
  BookOpen,
  Loader2,
  CopyX
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatNom, formatPrenom } from '../lib/personne';
import { CiviliteSelect } from '../components/CiviliteSelect';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CandidatsPage = () => {
  const [candidats, setCandidats] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingCandidat, setEditingCandidat] = useState(null);
  const [selectedCandidat, setSelectedCandidat] = useState(null);
  
  const [formData, setFormData] = useState({
    civilite: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    adresse: '',
    date_naissance: '',
    entreprise: ''
  });
  const [duplicatesFound, setDuplicatesFound] = useState([]);
  const [candidatActions, setCandidatActions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const [actionsMap, setActionsMap] = useState({});

  useEffect(() => {
    fetchCandidats();
    fetchActionsMap();
  }, []);

  const fetchActionsMap = async () => {
    try {
      const res = await axios.get(`${API_URL}/candidats/actions-map`);
      setActionsMap(res.data);
    } catch (e) {
      setActionsMap({});
    }
  };

  const fetchCandidats = async () => {
    try {
      const response = await axios.get(`${API_URL}/candidats`);
      setCandidats(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (candidat = null) => {
    if (candidat) {
      setEditingCandidat(candidat);
      setFormData({
        civilite: candidat.civilite || '',
        nom: candidat.nom,
        prenom: candidat.prenom,
        email: candidat.email,
        telephone: candidat.telephone || '',
        adresse: candidat.adresse || '',
        date_naissance: candidat.date_naissance || '',
        entreprise: candidat.entreprise || ''
      });
    } else {
      setEditingCandidat(null);
      setFormData({
        civilite: '',
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        adresse: '',
        date_naissance: '',
        entreprise: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCandidat) {
        await axios.put(`${API_URL}/candidats/${editingCandidat.id}`, formData);
        toast.success('Candidat mis à jour');
        setDialogOpen(false);
        setDuplicatesFound([]);
        fetchCandidats();
      } else {
        // Check duplicates first
        const checkRes = await axios.post(`${API_URL}/candidats/check-duplicate`, formData);
        if (checkRes.data.duplicates?.length > 0 && duplicatesFound.length === 0) {
          setDuplicatesFound(checkRes.data.duplicates);
          return;
        }
        await axios.post(`${API_URL}/candidats`, formData);
        toast.success('Candidat créé');
        setDialogOpen(false);
        setDuplicatesFound([]);
        fetchCandidats();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/candidats/${deleteId}`);
      setCandidats(candidats.filter(c => c.id !== deleteId));
      if (selectedCandidat && selectedCandidat.id === deleteId) {
        setSelectedCandidat(null);
      }
      toast.success('Candidat supprimé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleteId(null);
    }
  };

  const handleSelectCandidat = async (candidat) => {
    setSelectedCandidat(candidat);
    setCandidatActions([]);
    setLoadingActions(true);
    try {
      const res = await axios.get(`${API_URL}/candidats/${candidat.id}/actions`);
      setCandidatActions(res.data);
    } catch (e) {
      setCandidatActions([]);
    } finally {
      setLoadingActions(false);
    }
  };

  const handleEditFromDetail = () => {
    if (selectedCandidat) {
      setSelectedCandidat(null);
      handleOpenDialog(selectedCandidat);
    }
  };

  const handleDeleteFromDetail = () => {
    if (selectedCandidat) {
      setDeleteId(selectedCandidat.id);
    }
  };

  const getInitials = (nom, prenom) => {
    return `${nom?.[0] || ''}${prenom?.[0] || ''}`.toUpperCase();
  };

  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [showDoublons, setShowDoublons] = useState(false);

  const doublonIds = React.useMemo(() => {
    const byKey = {};
    candidats.forEach(c => {
      const np = `${(c.nom || '').trim().toLowerCase()}|${(c.prenom || '').trim().toLowerCase()}`;
      (byKey[np] = byKey[np] || []).push(c.id);
      if (c.email) { const em = `em:${c.email.trim().toLowerCase()}`; (byKey[em] = byKey[em] || []).push(c.id); }
    });
    return new Set(Object.values(byKey).filter(ids => ids.length > 1).flat());
  }, [candidats]);

  const filteredCandidats = candidats
    .filter(candidat =>
      (candidat.nom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (candidat.prenom || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (candidat.email || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const cmp = (a.nom || '').localeCompare(b.nom || '') || (a.prenom || '').localeCompare(b.prenom || '');
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="candidats-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Candidats</h1>
            <p className="text-slate-500 mt-1">{candidats.length} candidat(s)</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showDoublons ? 'default' : 'outline'}
              onClick={() => setShowDoublons(v => !v)}
              className={showDoublons ? 'bg-amber-500 hover:bg-amber-600' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}
              data-testid="toggle-doublons-btn"
            >
              <CopyX className="w-4 h-4 mr-2" />
              Doublons{doublonIds.size > 0 && !showDoublons ? ` (${doublonIds.size})` : ''}
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700" data-testid="new-candidat-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau candidat
            </Button>
          </div>
        </div>

        {showDoublons ? (
          <DoublonsCandidats onChanged={() => { fetchCandidats(); fetchActionsMap(); }} />
        ) : (
        <>
        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Rechercher un candidat..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-candidats"
          />
        </div>

        {/* Candidats List */}
        {loading ? (
          <Card><CardContent className="p-6 space-y-3">{[1, 2, 3].map((i) => (<div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />))}</CardContent></Card>
        ) : filteredCandidats.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">Aucun candidat</h3>
              <p className="text-slate-500 mb-4">
                {searchTerm ? 'Aucun résultat pour cette recherche' : 'Ajoutez votre premier candidat'}
              </p>
              {!searchTerm && (
                <Button onClick={() => handleOpenDialog()} className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un candidat
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="grid grid-cols-[auto_1fr_1fr_140px_1fr] gap-4 bg-slate-100 border-b px-5 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wide">
              <span className="w-8"></span>
              <span
                className="cursor-pointer hover:text-slate-700 flex items-center gap-1 select-none"
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                data-testid="sort-by-nom"
              >
                Nom {sortOrder === 'asc' ? '↑' : '↓'}
              </span>
              <span className="hidden md:block">Email</span>
              <span className="hidden md:block">Téléphone</span>
              <span className="hidden lg:block">Actions de formation</span>
            </div>
            <div className="divide-y">
              {filteredCandidats.map((candidat) => {
                const afList = actionsMap[candidat.id] || [];
                return (
                <div
                  key={candidat.id}
                  className="grid grid-cols-[auto_1fr_1fr_140px_1fr] gap-4 items-center px-5 py-3 hover:bg-slate-50 cursor-pointer transition-colors"
                  data-testid={`candidat-row-${candidat.id}`}
                  onClick={() => handleSelectCandidat(candidat)}
                >
                  <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                    {getInitials(candidat.nom, candidat.prenom)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                      {candidat.nom} {candidat.prenom}
                      {doublonIds.has(candidat.id) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium" data-testid={`doublon-badge-${candidat.id}`}>Doublon</span>
                      )}
                    </p>
                    {candidat.entreprise && <p className="text-xs text-slate-400 truncate">{candidat.entreprise}</p>}
                  </div>
                  <p className="text-sm text-slate-500 truncate hidden md:block">{candidat.email}</p>
                  <p className="text-sm text-slate-500 hidden md:block">{candidat.telephone || '-'}</p>
                  <div className="hidden lg:flex flex-wrap gap-1">
                    {afList.length === 0 ? (
                      <span className="text-xs text-slate-300">-</span>
                    ) : afList.map((af, i) => (
                      <span key={i} className="text-[10px] font-mono bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded" title={af.intitule}>
                        {af.code}
                      </span>
                    ))}
                  </div>
                </div>
                );
              })}
            </div>
          </Card>
        )}
        </>
        )}

        {/* Candidat Detail Dialog */}
        <Dialog open={!!selectedCandidat} onOpenChange={(open) => !open && setSelectedCandidat(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {selectedCandidat && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center text-white text-xl font-semibold">
                      {getInitials(selectedCandidat.nom, selectedCandidat.prenom)}
                    </div>
                    <div>
                      <DialogTitle className="text-2xl font-['Plus_Jakarta_Sans']">
                        {selectedCandidat.nom} {selectedCandidat.prenom}
                      </DialogTitle>
                      {selectedCandidat.entreprise && (
                        <p className="text-slate-500 mt-1">{selectedCandidat.entreprise}</p>
                      )}
                    </div>
                  </div>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Email */}
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Mail className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">Email</p>
                        <p className="text-slate-900">{selectedCandidat.email}</p>
                      </div>
                    </div>

                    {/* Téléphone */}
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Phone className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">Téléphone</p>
                        <p className="text-slate-900">{selectedCandidat.telephone || '-'}</p>
                      </div>
                    </div>

                    {/* Entreprise */}
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Building className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">Entreprise</p>
                        <p className="text-slate-900">{selectedCandidat.entreprise || '-'}</p>
                      </div>
                    </div>

                    {/* Date de naissance */}
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Calendar className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">Date de naissance</p>
                        <p className="text-slate-900">{selectedCandidat.date_naissance || '-'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Adresse */}
                  {selectedCandidat.adresse && (
                    <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-slate-600 mt-0.5" />
                      <div>
                        <p className="text-xs font-medium text-slate-500">Adresse</p>
                        <p className="text-slate-900">{selectedCandidat.adresse}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions de formation */}
                  <div className="border-t pt-4" data-testid="candidat-actions-section">
                    <div className="flex items-center gap-2 mb-3">
                      <BookOpen className="w-4 h-4 text-indigo-500" />
                      <h4 className="text-sm font-semibold text-slate-700">Actions de formation</h4>
                    </div>
                    {loadingActions ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                        <span className="text-xs text-slate-400 ml-2">Chargement...</span>
                      </div>
                    ) : candidatActions.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">Aucune action de formation</p>
                    ) : (
                      <div className="space-y-2">
                        {candidatActions.map(a => (
                          <div
                            key={a.id}
                            className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2 hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors cursor-pointer"
                            onClick={() => window.location.href = `/dossiers/${a.id}`}
                            data-testid={`candidat-action-${a.id}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <span className="text-xs font-mono text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded whitespace-nowrap">{a.code}</span>
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-800 truncate">{a.intitule || a.formation_titre || 'Sans intitulé'}</p>
                                {a.formation_code && <p className="text-[10px] text-slate-400">{a.formation_code} — {a.formation_titre}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <CandidatFicheActions dossierId={a.id} candidatId={selectedCandidat.id} nom={selectedCandidat.nom} prenom={selectedCandidat.prenom} apiBase={`${API_URL}/portail/admin`} />
                              {a.date_debut && <span className="text-[10px] text-slate-400">{a.date_debut}</span>}
                              <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                a.statut_administratif === 'en_cours' ? 'bg-blue-100 text-blue-700' :
                                a.statut_administratif === 'termine' ? 'bg-emerald-100 text-emerald-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {a.statut_administratif === 'brouillon' ? 'Brouillon' : a.statut_administratif === 'en_cours' ? 'En cours' : a.statut_administratif === 'termine' ? 'Terminé' : a.statut_administratif || 'N/A'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter className="flex gap-2 sm:gap-2">
                  <Button 
                    variant="outline" 
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    onClick={handleDeleteFromDetail}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleEditFromDetail}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Candidat Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Plus_Jakarta_Sans']">
                {editingCandidat ? 'Modifier le candidat' : 'Nouveau candidat'}
              </DialogTitle>
              <DialogDescription>
                {editingCandidat ? 'Modifiez les informations du candidat' : 'Ajoutez un nouveau candidat'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex justify-end -mt-2">
                <Button type="submit" size="sm" className={duplicatesFound.length > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'} data-testid="submit-candidat-top">
                  {editingCandidat ? 'Mettre à jour' : duplicatesFound.length > 0 ? 'Créer quand même' : 'Créer'}
                </Button>
              </div>
              <div className="grid grid-cols-[110px_1fr_1fr] gap-4">
                <CiviliteSelect value={formData.civilite} onChange={(v) => setFormData({ ...formData, civilite: v })} testId="candidat-civilite" />
                <div className="space-y-2">
                  <Label htmlFor="prenom">Prénom *</Label>
                  <Input
                    id="prenom"
                    value={formData.prenom}
                    onChange={(e) => setFormData({ ...formData, prenom: formatPrenom(e.target.value) })}
                    required
                    data-testid="input-candidat-prenom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nom">Nom *</Label>
                  <Input
                    id="nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: formatNom(e.target.value) })}
                    required
                    data-testid="input-candidat-nom"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="input-candidat-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telephone">Téléphone</Label>
                  <Input
                    id="telephone"
                    type="tel"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    data-testid="input-candidat-telephone"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_naissance">Date de naissance</Label>
                  <Input
                    id="date_naissance"
                    type="date"
                    value={formData.date_naissance}
                    onChange={(e) => setFormData({ ...formData, date_naissance: e.target.value })}
                    data-testid="input-candidat-date-naissance"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="entreprise">Entreprise</Label>
                  <Input
                    id="entreprise"
                    value={formData.entreprise}
                    onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })}
                    data-testid="input-candidat-entreprise"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="adresse">Adresse</Label>
                <Input
                  id="adresse"
                  value={formData.adresse}
                  onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                  data-testid="input-candidat-adresse"
                />
              </div>

              {/* Doublons détectés */}
              {duplicatesFound.length > 0 && !editingCandidat && (
                <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="duplicate-warning-candidats">
                  <div className="flex items-center gap-2 text-amber-700">
                    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                    <p className="text-sm font-semibold">Doublon(s) détecté(s) !</p>
                  </div>
                  <p className="text-xs text-amber-600">Un ou plusieurs candidats similaires existent déjà dans la base. Cliquez "Créer quand même" pour forcer la création.</p>
                  <div className="space-y-1">
                    {duplicatesFound.map(dup => (
                      <div key={dup.id} className="flex items-center gap-3 bg-white border border-amber-200 rounded px-3 py-1.5 text-sm">
                        <span className="font-medium text-slate-800">{dup.nom} {dup.prenom}</span>
                        <span className="text-slate-500 text-xs">{dup.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); setDuplicatesFound([]); }}>
                  Annuler
                </Button>
                <Button type="submit" className={duplicatesFound.length > 0 ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'} data-testid="submit-candidat">
                  {editingCandidat ? 'Mettre à jour' : duplicatesFound.length > 0 ? 'Créer quand même' : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce candidat ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};
